import type { Item } from './item';
import type { Project } from './project';
import type { Scheduled } from './scheduled';

/** A transformation applied uniformly to every `Scheduled` value, whatever its value type. */
export type ScheduleMapper = <T>(scheduled: Scheduled<T>) => Scheduled<T>;

function mapItem(item: Item, fn: ScheduleMapper): Item {
  switch (item.type) {
    case 'flow': {
      const growth =
        item.growth.kind === 'rate'
          ? { ...item.growth, rate: fn(item.growth.rate) }
          : item.growth.kind === 'indexed' && item.growth.override
            ? { ...item.growth, override: fn(item.growth.override) }
            : item.growth;
      return { ...item, amount: fn(item.amount), growth };
    }
    case 'formula': {
      const spec = item.spec;
      if (spec.kind === 'percentOf')
        return { ...item, spec: { ...spec, percent: fn(spec.percent) } };
      return {
        ...item,
        spec: {
          ...spec,
          brackets: fn(spec.brackets),
          ...(spec.allowance ? { allowance: fn(spec.allowance) } : {}),
          ...(spec.credits ? { credits: fn(spec.credits) } : {}),
          deductions: spec.deductions.map((d) => ({
            ...d,
            rate: fn(d.rate),
            ...(d.floor ? { floor: fn(d.floor) } : {}),
            ...(d.cap ? { cap: fn(d.cap) } : {}),
          })),
        },
      };
    }
    case 'asset':
      return { ...item, return: fn(item.return) };
    case 'loan':
      return { ...item, rate: fn(item.rate) };
  }
}

/** Applies `fn` to every schedule in the project (CPI, overdraft, categories, items). */
export function mapScheduled(project: Project, fn: ScheduleMapper): Project {
  const a = project.assumptions;
  return {
    ...project,
    assumptions: {
      ...a,
      cpi: fn(a.cpi),
      ...(a.overdraftRate ? { overdraftRate: fn(a.overdraftRate) } : {}),
    },
    categories: project.categories.map((c) =>
      c.inflation ? { ...c, inflation: fn(c.inflation) } : c,
    ),
    items: Object.fromEntries(
      Object.entries(project.items).map(([k, item]) => [k, mapItem(item, fn)]),
    ),
  };
}

/**
 * Drops schedule periods that start at or after `months` (e.g. when the horizon is shortened).
 * A schedule left with a single period from month 0 becomes a constant.
 */
export function trimSchedule<T>(s: Scheduled<T>, months: number): Scheduled<T> {
  if (s.kind === 'constant') return s;
  const segments = s.segments.filter((seg) => seg.from < months);
  if (segments.length === s.segments.length) return s;
  const first = segments[0];
  if (!first) return s.fallback === undefined ? s : { kind: 'constant', value: s.fallback };
  if (segments.length === 1 && first.from === 0) return { kind: 'constant', value: first.value };
  return { ...s, segments };
}

export function trimSchedulesToHorizon(project: Project, months: number): Project {
  return mapScheduled(project, (s) => trimSchedule(s, months));
}
